// Account.js
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { io } from "socket.io-client";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styles from "./Account.module.css";
import { apiFetch, getAccessToken } from "../utils/auth";
import { getRangiDeviceId } from "../utils/deviceId";
import { API_URL, SOCKET_URL } from '../config';
import { useEmployeeId } from "../context/RoleContext";
import backIcon from "../icons/leave_account_icon.svg";
import leaguesIcon from "../icons/leagues_icon.svg";
import { ReactComponent as OpenChatIcon } from "../icons/account_open_chat.svg";
import { ReactComponent as BirthdayIcon } from "../icons/birthday_date_icon.svg";
import { ReactComponent as WorkingDaysIcon } from "../icons/working_days_icon.svg";
import { Avatar, EmployeeTaskStatsAvatar } from "./UI";
import { useEmployeeTaskStats } from "../utils/employeeTaskStats";

const API_BASE = (API_URL || "").replace(/\/api\/v\d+\/?$/, "");
const resolveImageUrl = (path) =>
  !path ? null : /^https?:\/\//i.test(path) ? path : `${API_BASE}${path}`;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

// Получаем инициалы из полного имени
function getInitials(fullName) {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

const TABS = [
  { id: "profile", label: "Профиль" },
  { id: "achievements", label: "Достижения" },
  { id: "labels", label: "Метки" },
  { id: "medals", label: "Медали" },
];

export default function Account() {
  const socketRef = useRef(null);
  const navigate = useNavigate();
  const params = useParams();
  const query = useQuery();
  const currentEmployeeId = useEmployeeId();

  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [rankMap, setRankMap] = useState({});

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);

  const [about, setAbout] = useState("");
  const [aboutEdit, setAboutEdit] = useState(false);
  const [savingAbout, setSavingAbout] = useState(false);

  const isMeRoute = params.employeeId === undefined || params.employeeId === "me";
  const employeeIdForChat = useMemo(() => (data?.id ? data.id : null), [data]);

  const profileEmployeeId = useMemo(() => {
    if (data?.id != null) return Number(data.id);
    if (isMeRoute && currentEmployeeId != null) return Number(currentEmployeeId);
    const fromParams = Number(params.employeeId);
    return Number.isFinite(fromParams) && fromParams > 0 ? fromParams : null;
  }, [data?.id, isMeRoute, currentEmployeeId, params.employeeId]);

  const employeeTaskStatsIds = useMemo(
    () => (profileEmployeeId ? [profileEmployeeId] : []),
    [profileEmployeeId]
  );
  const employeeTaskStatsMap = useEmployeeTaskStats(employeeTaskStatsIds);

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const endpoint = isMeRoute
        ? `${API_URL}/employees/me/account`
        : `${API_URL}/employees/${params.employeeId}/account`;

      const [accRes, ranksRes] = await Promise.all([
        apiFetch(endpoint),
        apiFetch(`${API_URL}/structure/ranks`),
      ]);

      if (!accRes.ok) {
        let msg = "Не удалось загрузить аккаунт";
        try { const j = await accRes.json(); msg = j?.detail || msg; } catch { }
        throw new Error(msg);
      }

      let map = {};
      if (ranksRes.ok) {
        try {
          const ranksJson = await ranksRes.json();
          for (const r of (ranksJson || [])) map[Number(r.id)] = r.name;
        } catch { }
      }
      setRankMap(map);

      const j = await accRes.json();
      setData(j || {});
    } catch (e) {
      setError(e?.message || "Ошибка");
    } finally {
      setLoading(false);
    }
  }, [isMeRoute, params.employeeId]);

  useEffect(() => {
    setAbout((data?.about_me || "").trim());
  }, [data?.about_me]);

  useEffect(() => {
    document.body.classList.add('account-page-open');

    return () => {
      document.body.classList.remove('account-page-open');
    };
  }, []);

  const saveAbout = useCallback(async () => {
    const trimmed = (about || "").trim();
    if (trimmed.length > 500) { alert("Не больше 500 символов"); return; }
    setSavingAbout(true);
    try {
      const res = await apiFetch(`${API_URL}/employees/me/about`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ about: trimmed }),
      });
      if (!res.ok) {
        let msg = "Не удалось сохранить «О себе»";
        try { msg = (await res.json())?.detail || msg; } catch { }
        throw new Error(msg);
      }
      const j = await res.json();
      setData(prev => ({ ...(prev || {}), about_me: j?.about_me ?? trimmed }));
      setAboutEdit(false);
    } catch (e) {
      alert(e.message || "Ошибка сети");
    } finally {
      setSavingAbout(false);
    }
  }, [about]);

  useEffect(() => {
    const els = [window, document.scrollingElement, document.documentElement, document.body, document.getElementById('root')].filter(Boolean);
    els.forEach((el) => { try { (el.scrollTo ? el : window).scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { } });
  }, []);

  useEffect(() => { fetchAccount(); }, [fetchAccount]);

  useEffect(() => {
    if (!data?.id) return;
    if (!currentEmployeeId) return;
    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL, {
        path: "/ws/socket.io",
        transports: ["websocket", "polling"],
        withCredentials: true,
        auth: (cb) => {
          cb({
            token: getAccessToken(),
            device_id: getRangiDeviceId(),
          });
        },
      });
      socketRef.current.on("connect_error", () => { });
    }
    const socket = socketRef.current;
    const onReordered = ({ type, ordered_ids } = {}) => {
      if (type && Array.isArray(ordered_ids) && ordered_ids.length && data) {
        setData(prev => {
          if (!prev) return prev;
          const field = type === "medal" ? "medals" : type === "label" ? "labels" : "achievements";
          const index = new Map(ordered_ids.map((id, i) => [id, i]));
          const src = Array.isArray(prev[field]) ? prev[field] : [];
          const reordered = [...src].sort((a, b) => {
            const ai = index.get(a.achievement_id) ?? 1e9;
            const bi = index.get(b.achievement_id) ?? 1e9;
            if (ai !== bi) return ai - bi;
            const at = a.awarded_at || "", bt = b.awarded_at || "";
            if (at !== bt) return at < bt ? -1 : 1;
            return (a.id ?? 0) - (b.id ?? 0);
          });
          return { ...prev, [field]: reordered };
        });
        return;
      }
      fetchAccount(true);
    };
    socket.on("achievements_reordered", onReordered);
    return () => {
      socket.off("achievements_reordered", onReordered);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [data?.id, fetchAccount, currentEmployeeId]);

  const openChat = useCallback(async () => {
    if (!employeeIdForChat || chatLoading) return;
    setChatLoading(true);
    try {
      const fd = new FormData();
      fd.append('chat_type', 'private');
      fd.append('name', data?.full_name || '');
      fd.append('participant_ids', String(employeeIdForChat));
      const res = await apiFetch(`${API_URL}/chats/`, { method: 'POST', body: fd });
      if (res.status === 200 || res.status === 201) {
        const j = await res.json();
        if (j?.id) navigate(`/chats?open=${j.id}`);
        return;
      }
      if (res.status === 409) {
        const j = await res.json().catch(() => ({}));
        const chatId = j?.chat_id || j?.id;
        if (chatId) { navigate(`/chats?open=${chatId}`); return; }
      }
      console.warn('Cannot open private chat');
    } catch (e) { console.error(e); }
    finally { setChatLoading(false); }
  }, [employeeIdForChat, chatLoading, navigate]);

  const onBadgeClick = (badge) => { setSelectedBadge(badge); setSheetOpen(true); };

  // ---- LOADING ----
  if (loading) {
    return (
      <div className={styles.wrap}>
        {/* Скелетон шапки */}
        <div className={styles.header}>
          <div className={styles.headerTop}>
            <div className={`${styles.skel} ${styles.skelBtn}`} style={{ width: 24, height: 24, borderRadius: 6 }} />
            <div className={`${styles.skel} ${styles.skelLogo}`} />
          </div>
          <div className={styles.headerMain}>
            <div className={`${styles.skel} ${styles.skelAvatar}`} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className={`${styles.skel} ${styles.skelRank}`} />
              <div className={`${styles.skel} ${styles.skelName}`} />
              <div style={{ display: 'flex', gap: 6 }}>
                <div className={`${styles.skel} ${styles.skelChip}`} />
                <div className={`${styles.skel} ${styles.skelChip}`} />
              </div>
            </div>
          </div>
        </div>
        {/* Скелетон табов */}
        <div className={styles.tabs} style={{ gap: 0 }}>
          {TABS.map(t => <div key={t.id} className={`${styles.skel}`} style={{ width: 80, height: 10, margin: '14px 18px', borderRadius: 6 }} />)}
        </div>
        {/* Скелетон контента */}
        <div style={{ padding: '14px 14px 0' }}>
          <div className={`${styles.skel} ${styles.skelCard}`} style={{ marginBottom: 10 }} />
          <div className={`${styles.skel} ${styles.skelCard}`} style={{ marginBottom: 10 }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.wrap}>
        <div className={styles.error}>{error}</div>
        <button className={styles.retry} onClick={fetchAccount} style={{ marginLeft: 14, marginTop: 6 }}>Повторить</button>
      </div>
    );
  }

  if (!data) {
    return <div className={styles.wrap}><div className={styles.error}>Сотрудник не найден</div></div>;
  }

  const {
    rank, league, full_name, department_code,
    team_logo_url, birthday_ddmm, employment_days,
    avatar_url, manager,
    medals = [], labels = [], achievements = [],
    about_me, side,
  } = data;

  // Текст ранга
  const rankId = Number(rank?.id);
  const rankNameFromMap = Number.isFinite(rankId) ? rankMap[rankId] : null;
  const rankText = (rankNameFromMap && String(rankNameFromMap).trim()) || (rank?.name && String(rank.name).trim()) || "";
  const leagueText = league?.name ? String(league.name).trim() : "";

  const profileAvatar = (
    <EmployeeTaskStatsAvatar
      employeeId={data.id}
      stats={employeeTaskStatsMap.get(data.id)}
      fetchStats={false}
      avatar={avatar_url}
      size={150}
      initials={getInitials(full_name)}
      alt={full_name || "avatar"}
      onClick={avatar_url ? () => setAvatarModalOpen(true) : undefined}
      className={avatar_url ? styles.headerAvatarClickable : ""}
    />
  );

  const mobileProfileAvatar = (
    <div
      className={`${styles.headerAvatarMobileInner} ${avatar_url ? styles.headerAvatarClickable : ""}`}
      onClick={() => { if (avatar_url) setAvatarModalOpen(true); }}
      onKeyDown={(e) => {
        if (avatar_url && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setAvatarModalOpen(true);
        }
      }}
      role={avatar_url ? 'button' : undefined}
      tabIndex={avatar_url ? 0 : undefined}
      aria-label={avatar_url ? 'Открыть фото' : undefined}
    >
      <Avatar
        src={resolveImageUrl(avatar_url)}
        name={full_name}
        initials={getInitials(full_name)}
        alt={full_name || "avatar"}
        className={styles.headerAvatarImg}
        fallbackClassName={styles.headerAvatarFallback}
      />
    </div>
  );

  return (
    <div className={styles.wrap}>
      {/* ===== ШАПКА ===== */}
      <div className={styles.header}>
        {/* Верхняя строка: назад + логотип */}
        <div className={styles.headerTop}>
          <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Назад">
            <img src={backIcon} alt="Назад" className={styles.backIcon} />
          </button>
        </div>

        <div className={styles.headerMain}>
          <div className={styles.headerAvatar}>
            {profileAvatar}
          </div>

          <div className={styles.headerInfo}>
            {(rankText || leagueText) && (
              <div className={styles.rank}>
                {rankText && <span>{rankText}</span>}
                {rankText && leagueText && (
                  <img src={leaguesIcon} alt="" className={styles.leagueSepIcon} />
                )}
                {leagueText && <span>{leagueText}</span>}
              </div>
            )}

            <div className={styles.name}>{full_name || "—"}</div>

            {side && (
              <div className={styles.side}>
                ({side === 'dark' ? 'тёмный' : 'светлый'})
              </div>
            )}

            {department_code && (
              <span className={styles.deptChip}>Отдел: {department_code}</span>
            )}
            <div className={styles.metaChips}>
              {birthday_ddmm && (
                <span className={styles.chip}>
                  <BirthdayIcon />
                  {birthday_ddmm}
                </span>
              )}
              {employment_days && (
                <span className={styles.chip}>
                  <WorkingDaysIcon />
                  {employment_days}
                </span>
              )}
            </div>
          </div>
          <div className={styles.headerActionsContainer}>
            {team_logo_url && (
              <div className={styles.teamLogoContainer}>
                <img className={styles.teamLogoImg} src={resolveImageUrl(team_logo_url)} alt="team" />
              </div>
            )}
            {!isMeRoute && (
              <div className={styles.headerActions}>
                <button className={styles.chatBtn} onClick={openChat} disabled={chatLoading}>
                  <OpenChatIcon className={styles.toChatIcon} aria-hidden="true" />
                  {chatLoading ? 'Открываем…' : 'В беседу'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Кнопка "В беседу" — только для чужих профилей */}


        <div className={styles.headerAvatarMobile}>
          {mobileProfileAvatar}
        </div>
      </div>

      {/* ===== КОНТЕНТ АККАУНТА (всё на одном экране) ===== */}
      <div className={styles.tabContent}>
        {/* О себе */}
        {(isMeRoute || (about_me && about_me.trim().length > 0)) && (
          <div className={styles.aboutCard}>
            <div className={styles.aboutCardHeader}>
              <span className={styles.aboutCardLabel}>О себе</span>
              {isMeRoute && (
                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={() => setAboutEdit(v => !v)}
                  title={aboutEdit ? "Отменить" : "Редактировать"}
                >
                  ✎
                </button>
              )}
            </div>

            {aboutEdit ? (
              <>
                <textarea
                  className={styles.aboutTextarea}
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  maxLength={500}
                  placeholder="Расскажите о себе (до 500 символов)…"
                />
                <div className={styles.aboutFooter}>
                  <span className={styles.aboutCounter}>{about.length}/500</span>
                  <div className={styles.aboutBtns}>
                    <button
                      className={styles.retry}
                      onClick={() => { setAboutEdit(false); setAbout((about_me || "").trim()); }}
                    >
                      Отменить
                    </button>
                    <button
                      className={styles.aboutSave}
                      disabled={savingAbout}
                      onClick={saveAbout}
                    >
                      {savingAbout ? "Сохраняю…" : "Сохранить"}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              (about_me && about_me.trim()) ? (
                <div className={styles.aboutMe}>{about_me.trim()}</div>
              ) : (
                <div className={styles.aboutMeMuted}>Не заполнено</div>
              )
            )}
          </div>
        )}

        {/* Сетка: руководитель + отдел */}
        <div className={styles.infoGrid}>
          <div className={styles.infoCard}>
            <div className={styles.infoCardLabel}>Руководитель</div>
            {manager?.id ? (
              <div
                className={styles.managerNameLink}
                onClick={() => navigate(`/account/${manager.id}`)}
                title="Открыть аккаунт"
              >
                {manager.full_name || "—"}
              </div>
            ) : (
              <div className={styles.infoCardValue}>{manager?.full_name || "—"}</div>
            )}
          </div>
        </div>

        {achievements.length > 0 && (
          <>
            <BadgeRow title="Достижения" items={achievements} onClick={onBadgeClick} />
          </>
        )}

        {labels.length > 0 && (
          <>
            <BadgeRow title="Метки" items={labels} onClick={onBadgeClick} />
          </>
        )}

        {medals.length > 0 && (
          <BadgeRow title="Медали" items={medals} onClick={onBadgeClick} />
        )}
      </div>

      {/* ===== MODALS ===== */}
      <BadgeModal open={sheetOpen} onClose={() => setSheetOpen(false)} badge={selectedBadge} />
      <AvatarModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        src={avatar_url ? resolveImageUrl(avatar_url) : null}
        alt={full_name || "avatar"}
      />
    </div>
  );
}

/* ========== Мелкие компоненты ========== */

function BadgeRow({ title, items, onClick }) {
  return (
    <div className={styles.badgeSection}>
      <div className={styles.badgeTitle}>{title}</div>
      <div className={styles.badgeRow}>
        {items.map((b) => (
          <button key={b.id} className={styles.badgeItem} onClick={() => onClick(b)} title={b.title || ""}>
            {b.image_url ? (
              <img src={resolveImageUrl(b.image_url)} alt={b.title || "badge"} />
            ) : (
              <div className={styles.badgeFallback}>—</div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyTab({ text }) {
  return (
    <div className={styles.emptyTab}>{text}</div>
  );
}

function BadgeModal({ open, onClose, badge }) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open || !badge) return null;

  return (
    <>
      <div className={styles.modalBackdrop} onClick={onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true">
        <div className={styles.modalHeader}>
          <span className={styles.modalTypeLabel}>
            {badge.type === "medal" ? "Медаль" : badge.type === "label" ? "Метка" : "Достижение"}
          </span>
          <button className={styles.modalClose} onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        <div className={styles.modalBody}>
          {badge.image_url && (
            <img
              className={styles.modalImage}
              src={resolveImageUrl(badge.image_url)}
              alt={badge.title || "badge"}
            />
          )}
          <div className={styles.modalTitle}>{badge.title || "Без названия"}</div>
          {badge.description ? (
            <div className={styles.modalDesc}>{badge.description}</div>
          ) : (
            <div className={styles.modalDescMuted}>Описание отсутствует</div>
          )}
        </div>
      </div>
    </>
  );
}

function AvatarModal({ open, onClose, src, alt }) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open || !src) return null;

  return (
    <div className={styles.avatarModal} onClick={onClose}>
      <img
        className={styles.avatarModalImage}
        src={src}
        alt={alt || "avatar"}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}